/**
 * Shared types and constants for ATC Visibility feature
 * Used by both frontend and backend to ensure consistency
 */

const TIME_INTERVALS = [
  '5m',
  '15m',
  '30m',
  '1h',
  '2h',
  '5h',
  '12h',
  '24h',
  '48h',
] as const;

export type TimeInterval = (typeof TIME_INTERVALS)[number];

// Consolidated interval metadata - single source of truth
const TIME_INTERVAL_CONFIG: Record<
  TimeInterval,
  { label: string; sqlInterval: string }
> = {
  '5m': { label: 'Last 5 minutes', sqlInterval: '5 minutes' },
  '15m': { label: 'Last 15 minutes', sqlInterval: '15 minutes' },
  '30m': { label: 'Last 30 minutes', sqlInterval: '30 minutes' },
  '1h': { label: 'Last 1 hour', sqlInterval: '1 hour' },
  '2h': { label: 'Last 2 hours', sqlInterval: '2 hours' },
  '5h': { label: 'Last 5 hours', sqlInterval: '5 hours' },
  '12h': { label: 'Last 12 hours', sqlInterval: '12 hours' },
  '24h': { label: 'Last 24 hours', sqlInterval: '24 hours' },
  '48h': { label: 'Last 48 hours', sqlInterval: '48 hours' },
};

// Convenience accessors derived from TIME_INTERVAL_CONFIG
export const TIME_INTERVAL_TO_SQL: Record<TimeInterval, string> =
  Object.fromEntries(
    Object.entries(TIME_INTERVAL_CONFIG).map(([key, val]) => [
      key,
      val.sqlInterval,
    ])
  ) as Record<TimeInterval, string>;

export const TIME_INTERVAL_OPTIONS: Array<{
  value: TimeInterval;
  label: string;
}> = TIME_INTERVALS.map((interval) => ({
  value: interval,
  label: TIME_INTERVAL_CONFIG[interval].label,
}));

/**
 * Safety ceiling on a single "retry as new" action: the most failed ATC runs
 * we'll resubmit in one request. This is a backstop against an accidental
 * mass operation (each re-run creates a new run that executes), not a normal
 * limit — above it the UI blocks and the endpoint refuses rather than silently
 * dropping runs. Shared so the client warning and the server guard agree.
 */
export const ATC_RETRY_MAX_BATCH = 2000;
